import type Highcharts from "highcharts";

import {
  greedyCullVisibleFlags,
  minPixelDistanceFromMarkerRadius,
} from "@/lib/graph/greedy-cull-by-pixel-distance";
import { nestChartRenderEvent } from "@/lib/graph/nest-chart-render-event";

type PointWithVectorOpacityCull = Highcharts.Point & {
  _vectorOpacityCulled?: boolean;
};

function isPointHoveredByChart(chart: Highcharts.Chart, point: Highcharts.Point): boolean {
  if (chart.hoverPoint === point) {
    return true;
  }
  const hoverPoints = chart.hoverPoints;
  return !!(hoverPoints && hoverPoints.some((p) => p === point));
}

function applyVectorOpacityCullToChart(chart: Highcharts.Chart): void {
  const markerRadius = chart.options.plotOptions?.series?.marker?.radius || 4;
  const markerBased = minPixelDistanceFromMarkerRadius(markerRadius);
  const vectorLengthPx = chart.options.plotOptions?.vector?.vectorLength ?? 22;
  const minPixelDistance = Math.max(markerBased, vectorLengthPx);

  chart.series.forEach((series) => {
    if (series.type !== "vector") return;

    const nonEmptyPoints = series.points.filter(
      (point) => point.y !== null && point.y !== undefined,
    );

    const visible = greedyCullVisibleFlags(nonEmptyPoints, chart, minPixelDistance);

    nonEmptyPoints.forEach((point, index) => {
      const show = visible[index];
      const graphic = point.graphic;
      if (!graphic) return;

      const p = point as PointWithVectorOpacityCull;
      p._vectorOpacityCulled = !show;
      const hovered = isPointHoveredByChart(chart, point);

      graphic.attr({
        opacity: show || hovered ? 1 : 0,
        "pointer-events": "all",
      });
    });
  });
}

function mergeVectorHoverPlotOptions(
  plotOptions?: Highcharts.PlotOptions,
): Highcharts.PlotOptions {
  const caller = plotOptions;
  const vectorIn = caller?.vector && typeof caller.vector === "object" ? caller.vector : {};
  const pointIn =
    vectorIn.point && typeof vectorIn.point === "object" ? vectorIn.point : {};
  const eventsIn =
    pointIn.events && typeof pointIn.events === "object" ? pointIn.events : {};
  const prevMouseOver = eventsIn.mouseOver;
  const prevMouseOut = eventsIn.mouseOut;

  return {
    ...(caller ?? {}),
    vector: {
      ...vectorIn,
      point: {
        ...pointIn,
        events: {
          ...eventsIn,
          mouseOver: function (this: Highcharts.Point, event: Event) {
            prevMouseOver?.call(this, event);
            if (this.graphic) {
              this.graphic.attr({ opacity: 1 });
            }
          },
          mouseOut: function (this: Highcharts.Point, event: PointerEvent) {
            prevMouseOut?.call(this, event);
            const p = this as PointWithVectorOpacityCull;
            if (p._vectorOpacityCulled && this.graphic) {
              this.graphic.attr({ opacity: 0 });
            }
          },
        },
      },
    },
  };
}

/**
 * Merges vector hover handlers + appends opacity cull after any existing `render`.
 * Intended for vector charts only; harmless but pointless on other chart types.
 */
export function withVectorOpacityCull(options: Highcharts.Options): Highcharts.Options {
  const withPlot: Highcharts.Options = {
    ...options,
    plotOptions: mergeVectorHoverPlotOptions(options.plotOptions),
  };
  return nestChartRenderEvent(withPlot, function (this: Highcharts.Chart) {
    applyVectorOpacityCullToChart(this);
  });
}
